import React, { useCallback, useState, useEffect, memo, useRef, useMemo } from 'react';
import noop from 'lodash/noop';
import { graphql, useFragment } from 'react-relay';
import { layers } from '@jira/platform__ui-layers';
import { Box, xcss } from '@atlaskit/primitives';
import type {
	StatusTransition,
	StatusValue,
} from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { AsyncIssueStatusField } from '@atlassian/jira-issue-field-status/src/ui/async.tsx';
import { StatusLozenge as IssueStatusFieldReadonly } from '@atlassian/jira-issue-field-status/src/ui/status-lozenge/index.tsx';
import {
	fireTrackAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueStatusCell$key } from '@atlassian/jira-relay/src/__generated__/IssueStatusCell.graphql';
import { toIssueKey, toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useEditViewPopupModifiersContext } from '@atlassian/jira-issue-field-inline-edit-lite/src/common/controllers/modifiers-context/index.tsx';
import type { StatusUpdateHandler } from '@atlassian/jira-issue-table-hide-done-toggle/src/controllers/handle-status-update/types.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import { useHideDoneItems } from '../../../../controllers/hide-done-items/index.tsx';
import { getProjectKeyFromIssueKey } from '../../../utils/index.tsx';
import { toStatusValue, toJiraStatusField, type JiraStatusField } from './utils.tsx';

export type Props = {
	fieldRef: IssueStatusCell$key;
	issueKey: string;
	issueId?: string;
	onUpdateCache?: (issueKey: string, fieldName: string, field: JiraStatusField) => void;
	recordId: string;
	itemsConnectionId: string;
	groupConnectionId?: string;
	isColumnSticky?: boolean;
};

export const IssueStatusCell = memo<Props>(
	({
		fieldRef,
		issueKey: issueKeyProp,
		issueId,
		onUpdateCache: onUpdateCacheProp,
		recordId,
		itemsConnectionId,
		groupConnectionId,
		isColumnSticky,
	}) => {
		let handleStatusUpdate: StatusUpdateHandler = noop;
		// eslint-disable-next-line jira/ff/no-preconditioning
		if (fg('jsc_list_hide_done_items') && fg('initialise_virtualiser_state_from_relay_store')) {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			handleStatusUpdate = useHideDoneItems({
				itemsConnectionId,
				recordId,
				projectKey: issueKeyProp && getProjectKeyFromIssueKey(issueKeyProp),
				groupConnectionId,
			});
		}

		const handleUpdateCache =
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jsc_list_hide_done_items') && fg('initialise_virtualiser_state_from_relay_store')
				? // eslint-disable-next-line react-hooks/react-compiler
					// eslint-disable-next-line react-hooks/rules-of-hooks
					useCallback<(issueKey: string, fieldName: string, field: JiraStatusField) => void>(
						(issueKey, fieldName, field) => {
							onUpdateCacheProp?.(issueKey, fieldName, field);
							handleStatusUpdate(field);
						},
						[handleStatusUpdate, onUpdateCacheProp],
					)
				: onUpdateCacheProp;

		const data = useFragment<IssueStatusCell$key>(
			graphql`
				fragment IssueStatusCell on JiraStatusField
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					id @include(if: $isInlineEditingEnabled)
					status {
						name
						statusId
						statusCategory {
							statusCategoryId
						}
					}
				}
			`,

			fieldRef,
		);

		const { status } = data;
		const [value, setValue] = useState<JiraStatusField>(status);
		const [isEditing, setIsEditing] = useState(false);

		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
		const contentRef = useRef(null);

		useEffect(() => {
			setValue(status);
		}, [status]);

		const onSubmit = useCallback(
			(
				oldValue: StatusValue,
				newValue: StatusValue,
				meta: {
					transition: StatusTransition;
				},
				event: UIAnalyticsEvent,
			) => {
				// Mirrors the updated event fired from the issue view (with the addition of `isInlineEditing: true`) to
				// be used for core action tracking as defined in https://hello.atlassian.net/wiki/spaces/ANALYTICS/pages/3767029088/Monthly+Core+User+MCU+Definition
				fireTrackAnalytics(event, 'issueStatus updated', {
					oldValId: oldValue.id,
					oldStatusCategoryId: oldValue.statusCategory.id,
					newValId: newValue.id,
					newStatusCategoryId: newValue.statusCategory.id,
					hasScreen: meta.transition.hasScreen,
					isConditional: meta.transition.isConditional,
					isInlineEditing: true,
				});
			},
			[],
		);

		const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

		const modifiersFromContext = useEditViewPopupModifiersContext();

		const popperProps = useMemo(() => {
			const hasBasicFrozenColumns =
				// eslint-disable-next-line jira/ff/no-preconditioning
				fg('issue_table_frozen_columns') && fg('jira_list_frozen_column_bugfix');

			const isStickyColumn = fg('jira_timeline_sticky_assignee_status_columns') && isColumnSticky;

			if (isStickyColumn && hasBasicFrozenColumns) {
				return {
					modifiers: [
						{
							name: 'preventOverflow' as const,
							enabled: false,
						},
					],
					strategy: 'absolute' as const,
					innerRef: (element: HTMLElement | null) => {
						if (element) {
							// setting the zIndex to ensure the dropdown is not on top of the workflow modal
							// eslint-disable-next-line no-param-reassign
							element.style.zIndex = String(layers.modal - 1);
						}
					},
				};
			}

			return hasBasicFrozenColumns ? { modifiers: modifiersFromContext } : undefined;
		}, [modifiersFromContext, isColumnSticky]);

		if (
			value?.statusCategory?.statusCategoryId === undefined ||
			!value?.name?.length ||
			!value?.statusId?.length
		) {
			return <ErrorCell cellType="status" reason="Missing required fields" />;
		}

		const onSuccess = (newStatus: StatusValue) => {
			const transformedStatus = toJiraStatusField(newStatus);
			setValue(transformedStatus);
			handleUpdateCache && handleUpdateCache(issueKeyProp, 'status', transformedStatus);
		};

		let hoverPopoverRef: HoverPopoverContentRef = null;
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: toStatusValue(value).name,
			});
		}

		// If the property does not exist in fragment data then we know it has been conditionally excluded because
		// inline editing is disabled. In future this should follow the same design/deferred loading strategy of
		// other inline editable fields, however this would require a larger effort to decompose the status field.
		const isEditable = data && typeof data.id === 'string';

		if (!isSingleLineRowHeightEnabled)
			return (
				<Box
					xcss={fg('nin_issue_table_rtl_fix') ? alignmentFixStyles : alignmentFixStylesOld}
					data-vc="native-issue-table-ui-issue-status-box"
				>
					{isEditable ? (
						<AsyncIssueStatusField
							issueKey={toIssueKey(issueKeyProp)}
							issueId={issueId && toIssueId(issueId)}
							appearance="lozenge"
							value={toStatusValue(value)}
							onSubmit={onSubmit}
							onSuccess={onSuccess}
						/>
					) : (
						<IssueStatusFieldReadonly value={toStatusValue(value)} />
					)}
				</Box>
			);

		return hoverPopoverRef ? (
			<Box
				xcss={fg('nin_issue_table_rtl_fix') ? alignmentFixStyles : alignmentFixStylesOld}
				data-vc="native-issue-table-ui-issue-status-box"
				ref={isEditing ? () => hoverPopoverRef(null) : hoverPopoverRef}
			>
				{isEditable ? (
					<AsyncIssueStatusField
						popperProps={popperProps}
						issueKey={toIssueKey(issueKeyProp)}
						issueId={issueId && toIssueId(issueId)}
						appearance="lozenge"
						value={toStatusValue(value)}
						onSubmit={onSubmit}
						onSuccess={onSuccess}
						onEditStart={() => setIsEditing(true)}
						onEditCancel={() => setIsEditing(false)}
						{...(fg('nin-grid-keyboard-navigation') ? { shouldCaptureEscape: false } : {})}
					/>
				) : (
					<IssueStatusFieldReadonly value={toStatusValue(value)} />
				)}
			</Box>
		) : (
			<HoverPopoverWithContext
				content={toStatusValue(value).name}
				contentRef={contentRef}
				hidePopover={isEditing}
			>
				{({ ref }) => (
					<Box
						xcss={fg('nin_issue_table_rtl_fix') ? alignmentFixStyles : alignmentFixStylesOld}
						data-vc="native-issue-table-ui-issue-status-box"
						ref={ref}
					>
						{isEditable ? (
							<AsyncIssueStatusField
								issueKey={toIssueKey(issueKeyProp)}
								issueId={issueId && toIssueId(issueId)}
								appearance="lozenge"
								value={toStatusValue(value)}
								onSubmit={onSubmit}
								onSuccess={onSuccess}
								statusButtonRef={contentRef}
								onEditStart={() => setIsEditing(true)}
								onEditCancel={() => setIsEditing(false)}
								{...(fg('nin-grid-keyboard-navigation') ? { shouldCaptureEscape: false } : {})}
							/>
						) : (
							<IssueStatusFieldReadonly value={toStatusValue(value)} />
						)}
					</Box>
				)}
			</HoverPopoverWithContext>
		);
	},
);

// Status lozenges have a 2px invisible border around them to keep them in the same place if they enter an "error" state (where a border colour is added).
// Refer to: src/packages/issue/fields/status/src/common/ui/status-lozenge/main.tsx

const alignmentFixStyles = xcss({
	marginInlineStart: 'space.negative.025',
});

const alignmentFixStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	marginLeft: 'space.negative.025',
});
